import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, effect, inject } from "@angular/core";
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { AchievementType } from "@playcharge/common/models/backend/achievements/achievement-type";
import { EnumKeyValuePipe } from "../../../pipes/enumKeyValue.pipe";
import { MatExpansionModule } from "@angular/material/expansion";
import { ToastrService } from "ngx-toastr";
import { AchievementStore } from "../../../stores/achievements/achievement-store";
import {
    AchievementForm,
    AchievementTierForm,
} from "../../../models/achievements/achievement-forms";
import { Nullable } from "@playcharge/common/models/backend/nullable";
import { AchievementBackofficeViewModel } from "@playcharge/common/models/backend/achievements/backoffice/achievement-backoffice-view-model";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { ComponentRoutes } from "../../../models/constants/component-routes";
import { Router } from "@angular/router";
import { MatIconModule } from "@angular/material/icon";
import { AchievementTier } from "@playcharge/common/models/backend/achievements/achievement-tier";
import { NoScrollDirective } from "../../../directives/noScrollDirective";

@Component({
    selector: "app-achievement",
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatExpansionModule,
        MatCheckboxModule,
        MatButtonModule,
        MatIconModule,
        EnumKeyValuePipe,
        NoScrollDirective,
    ],
    providers: [AchievementStore],
    templateUrl: "./achievement.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AchievementComponent {
    private _toastrService: ToastrService = inject(ToastrService);
    private _router: Router = inject(Router);

    public achievementStore: AchievementStore = inject(AchievementStore);

    public form: FormGroup<AchievementForm> = new FormGroup<AchievementForm>({
        achievementId: new FormControl<number>({ value: 0, disabled: true }, { nonNullable: true }),
        achievementType: new FormControl<AchievementType>(AchievementType.None, {
            nonNullable: true,
        }),
        title: new FormControl<string>("", {
            nonNullable: true,
            validators: [Validators.required],
        }),
        logoFile: new FormControl<Nullable<File>>(null),
        order: new FormControl<number>(0, { nonNullable: true }),
        isActive: new FormControl<boolean>(false, { nonNullable: true }),
        tiers: new FormArray<FormGroup<AchievementTierForm>>([]),
    });

    public AchievementType: typeof AchievementType = AchievementType;

    constructor() {
        effect(() => this.achievementLoadedEffectFn());
    }

    public addTier(tier: Nullable<AchievementTier>) {
        this.form.controls.tiers.push(
            new FormGroup<AchievementTierForm>({
                tier: new FormControl<number>(tier?.tier ?? 0, { nonNullable: true }),
                description: new FormControl<string>(tier?.description ?? "", {
                    nonNullable: true,
                    validators: [Validators.required],
                }),
                logoUrl: new FormControl<string>(tier?.logoUrl ?? "", {
                    nonNullable: true,
                    validators: [Validators.required],
                }),
                reward: new FormControl<number>(tier?.reward ?? 0, { nonNullable: true }),
                expReward: new FormControl<number>(tier?.expReward ?? 0, { nonNullable: true }),
                amount: new FormControl<number>(tier?.amount ?? 0, { nonNullable: true }),
            }),
        );
    }

    public removeTier(tier: number): void {
        const index: number = this.form.controls.tiers.value.findIndex((x) => x.tier === tier);

        if (index === -1) {
            return;
        }

        this.form.controls.tiers.removeAt(index);
    }

    public createOrUpdateAchievement(): void {
        const formData = this.form.getRawValue();

        this.achievementStore.createOrUpdateAchievement(formData).subscribe({
            next: (achievement: AchievementBackofficeViewModel) => {
                if (this.achievementStore.achievementId()) {
                    this._toastrService.success("Successfully updated");
                } else {
                    this._router
                        .navigateByUrl(
                            ComponentRoutes.achievements.achievement.replace(
                                ":achievementId",
                                achievement.id.toString(),
                            ),
                        )
                        .then(() => this._toastrService.success("Successfully created"));
                }
            },
            error: (error) => {
                this._toastrService.error(error.error.description ?? "Internal Error");
            },
        });
    }

    private achievementLoadedEffectFn(): void {
        const achievement: Nullable<AchievementBackofficeViewModel> =
            this.achievementStore.achievement();

        if (achievement) {
            this.form.patchValue({ ...achievement, achievementId: achievement.id });

            if (!this.form.controls.tiers.length) {
                for (const tier of achievement.tiers) {
                    this.addTier(tier);
                }
            }
        }
    }
}
